import { INACTIVITY_MS, type LivePoint } from './chartIntervals'

/** Historia serii live: źródło prawdy dla wykresu czasu rzeczywistego.
 *
 *  Wykres nie rekonstruuje okresów nieaktywności ze swojego datasetu — dostaje je gotowe.
 *  Luka powstaje raz, w momencie przekroczenia `INACTIVITY_MS` od ostatniej próbki, i od tej
 *  chwili jest niezmienna poza domknięciem końca. Nowa wartość może lukę tylko zamknąć,
 *  nigdy skasować, więc równoczesne tyknięcie zegara i nadejście danych nie nadpisują się
 *  nawzajem.
 *
 *  Czas próbki to moment jej odebrania w przeglądarce, nie `createdAt` z ramki: próg liczy się
 *  od otrzymania danych i nie zależy od różnicy zegarów urządzenia i klienta. */

export type LiveSample = { t: number; value: number }

/** `to === null` oznacza lukę wciąż trwającą. */
export type LiveGap = { from: number; to: number | null }

export type LiveSeries = {
  startedAt: number
  /** Czy w chwili rozpoczęcia obserwacji telemetria urządzenia była świeża.
   *
   *  Pusta seria powstaje w dwóch zupełnie różnych sytuacjach: urządzenie milczy albo wykres
   *  otwarto przed chwilą i pierwsza ramka jeszcze do niego nie dotarła. Rozróżnia je ta sama
   *  informacja, po której kafelki uznają odczyt za aktualny. Bez niej wykres ogłaszał przestój
   *  nad pracującym urządzeniem, którego lampki świeciły się jako aktywne. */
  openedLive: boolean
  samples: LiveSample[]
  gaps: LiveGap[]
  lastSourceAt?: string
}

export type LiveRange = [number, number]

/** Okno retencji z zapasem ponad najdłuższe okno wykresu (5 min). */
export const LIVE_RETENTION_MS = 15 * 60_000

export const createLiveSeries = (now: number, openedLive = false): LiveSeries => ({
  startedAt: now,
  openedLive,
  samples: [],
  gaps: [],
})

/** Ile ciszy wolno przemilczeć, zanim otworzy się luka.
 *
 *  Pełny próg przysługuje serii, która ma już próbkę, oraz takiej, która zaczęła się przy świeżej
 *  telemetrii — w obu wypadkach istnieje odczyt uznawany za aktualny, więc cisza krótsza niż próg
 *  nie jest jeszcze przestojem. Seria otwarta nad milczącym urządzeniem nie ma czego przedłużać
 *  i dostaje zero, dzięki czemu wykres od razu mówi prawdę zamiast czekać trzy minuty. */
const graceOf = (series: LiveSeries): number => (series.samples.length > 0 || series.openedLive ? INACTIVITY_MS : 0)

const openGapOf = (series: LiveSeries): LiveGap | null => {
  const last = series.gaps[series.gaps.length - 1]
  return last && last.to === null ? last : null
}

const lastSampleOf = (series: LiveSeries): LiveSample | undefined => series.samples[series.samples.length - 1]

/** Punkt odniesienia dla progu bezczynności: ostatnia próbka, a przed pierwszą — start serii. */
export const liveReferenceOf = (series: LiveSeries): number => lastSampleOf(series)?.t ?? series.startedAt

/** Dopisuje próbkę i domyka ewentualną lukę. Zwraca `false`, gdy próbka została pominięta. */
export const appendLiveSample = (
  series: LiveSeries,
  sample: { t: number; value: number; sourceAt?: string },
): boolean => {
  if (!Number.isFinite(sample.t) || !Number.isFinite(sample.value)) return false
  if (sample.sourceAt !== undefined && sample.sourceAt === series.lastSourceAt) return false

  const last = lastSampleOf(series)
  if (last && sample.t <= last.t) return false

  const open = openGapOf(series)

  if (open) {
    open.to = Math.max(open.from, sample.t)
  } else {
    const reference = liveReferenceOf(series)

    /* Luka wykryta z samych czasów: karta w tle nie dostaje tyknięć zegara, więc próg mógł
       zostać przekroczony bez wywołania `sealLiveSeries`. Karencja liczona tą samą regułą. */
    const grace = graceOf(series)

    if (sample.t - reference >= grace) {
      series.gaps.push({ from: reference + grace, to: sample.t })
    }
  }

  series.samples.push({ t: sample.t, value: sample.value })
  if (sample.sourceAt !== undefined) series.lastSourceAt = sample.sourceAt

  return true
}

/** Otwiera lukę natychmiast, bez czekania na próg. Idempotentne.
 *
 *  Do powodów znanych od razu, a nie wnioskowanych z upływu czasu: urządzenie zgłoszone jako
 *  wyłączone przestaje dostarczać aktualne dane w tej samej chwili, w której gasną kontrolki.
 *
 *  Luka zaczyna się na ostatniej próbce, a nie w chwili zgłoszenia wyłączenia. To dwa różne
 *  zdarzenia: pomiary przychodzą rzadziej niż zmiana statusu, więc kotwiczenie w `now` zostawiało
 *  między końcem linii a początkiem szarego pasma odcinek, na którym wykres nie rysował ani danych,
 *  ani przestoju. `Math.min` zachowuje pierwotne zabezpieczenie na wypadek, gdyby znacznik próbki
 *  wyprzedził zegar — wtedy nadal wygrywa `now`. */
export const openLiveGap = (series: LiveSeries, now: number): boolean => {
  if (openGapOf(series)) return false

  series.gaps.push({ from: Math.min(now, liveReferenceOf(series)), to: null })

  return true
}

/** Otwiera lukę, gdy od punktu odniesienia minęła karencja. Idempotentne. Regułę karencji opisuje
 *  `graceOf`: seria pusta i otwarta nad milczącym urządzeniem dostaje zero i pokazuje przestój
 *  natychmiast, zamiast przez trzy minuty udawać, że wszystko jest w porządku. */
export const sealLiveSeries = (series: LiveSeries, now: number): boolean => {
  if (openGapOf(series)) return false

  const reference = liveReferenceOf(series)
  const grace = graceOf(series)
  if (now - reference < grace) return false

  series.gaps.push({ from: reference + grace, to: null })

  return true
}

/** Usuwa dane spoza okna retencji. Jedna próbka sprzed okna zostaje, żeby linia wchodziła w kadr. */
export const pruneLiveSeries = (series: LiveSeries, now: number, retentionMs = LIVE_RETENTION_MS): void => {
  const cutoff = now - retentionMs

  let outdated = 0
  while (outdated < series.samples.length && series.samples[outdated].t < cutoff) outdated += 1
  if (outdated > 1) series.samples.splice(0, outdated - 1)

  let staleGaps = 0
  while (staleGaps < series.gaps.length) {
    const gap = series.gaps[staleGaps]
    if (gap.to === null || gap.to >= cutoff) break
    staleGaps += 1
  }
  if (staleGaps > 0) series.gaps.splice(0, staleGaps)

  if (series.startedAt < cutoff) series.startedAt = cutoff
}

/** Luki w postaci zakresów do narysowania; luka trwająca kończy się na „teraz". */
export const resolveLiveGaps = (series: LiveSeries, now: number): LiveRange[] =>
  series.gaps.map((gap): LiveRange => [gap.from, gap.to ?? now]).filter(([from, to]) => to > from)

/** Dataset wykresu: próbki plus punkt `null` na początku każdej luki, żeby linia się przerwała. */
export const buildLiveDataset = (series: LiveSeries): LivePoint[] => {
  const points: LivePoint[] = []
  let gapIndex = 0

  const pushGapBreak = (limit: number) => {
    while (gapIndex < series.gaps.length && series.gaps[gapIndex].from <= limit) {
      points.push({ x: Math.min(series.gaps[gapIndex].from, limit), y: null })
      gapIndex += 1
    }
  }

  for (const sample of series.samples) {
    pushGapBreak(sample.t - 1)
    points.push({ x: sample.t, y: sample.value })
  }

  while (gapIndex < series.gaps.length) {
    points.push({ x: series.gaps[gapIndex].from, y: null })
    gapIndex += 1
  }

  return points
}

/** Wartość do etykiety: zero, gdy urządzenie jest nieaktywne.
 *
 *  Otwarta luka jest tym samym faktem, który maluje na wykresie pasmo przestoju — wiążemy z nią
 *  wartość, żeby legenda i tło nie mogły mówić czegoś innego. Sam próg bezczynności nie wystarczał:
 *  urządzenie zgłoszone jako wyłączone otwiera lukę natychmiast, a wartość trzymała się jeszcze
 *  przez trzy minuty. */
export const lastLiveValue = (series: LiveSeries, now: number): number => {
  if (openGapOf(series)) return 0

  const last = lastSampleOf(series)

  return last && now - last.t <= INACTIVITY_MS ? last.value : 0
}

/** Lewa krawędź tego, co seria pamięta. Służy za dolne ograniczenie pasm stanu urządzenia.
 *
 *  Celowo `startedAt`, a nie pierwsza próbka. Kotwiczenie w pierwszej próbce sprawiało, że krawędź
 *  przeskakiwała w chwili nadejścia danych: wykres otwarty przy wyłączonym urządzeniu pokazywał
 *  pasek alarmu od momentu wejścia, a pierwsza próbka przycinała go wstecznie do swojego znacznika
 *  i historia alarmu sprzed danych znikała. Gdy dane i koniec alarmu przychodziły tą samą ramką,
 *  z paska nie zostawało nic.
 *
 *  `pruneLiveSeries` podnosi `startedAt` do progu retencji, więc krawędź nie ucieka w przeszłość. */
export const liveSeriesStart = (series: LiveSeries): number => series.startedAt
